// frontend/src/components/profile/userProfile/UserProfile.jsx
import axios from "axios";
import { Context } from "../../../context/Context";
import "./userProfile.css";
import React, { useContext, useEffect, useState } from "react";
import { toast } from "react-toastify";

const UserProfile = ({ onSave }) => {
  const { user } = useContext(Context);

  const [formData, setFormData] = useState({});
  const [showPasswords, setShowPasswords] = useState(false);
  const [showPasswordSection, setShowPasswordSection] = useState(false);

  useEffect(() => {
    const getUserData = async () => {
      try {
        const res = await axios.get(`/users/${user.userId}`);
        if (res.status !== 200) {
          toast.info(res.data.msg);
        }
        const userData = res.data;

        if (userData.dob) {
          const dateObj = new Date(userData.dob);
          if (!isNaN(dateObj)) {
            userData.dob = dateObj.toISOString().split("T")[0]; // "YYYY-MM-DD"
          }
        }

        setFormData(userData);
      } catch (error) {
        console.log("Error fetching Form Data.", error);
        toast.error("Error Fetching User Data");
      }
    };
    getUserData();
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    try {
      if (formData.oldPassword || formData.newPassword || formData.confirmPassword) {
        if (!formData.oldPassword || !formData.newPassword || !formData.confirmPassword) {
          toast.error("All password fields are required.");
          return;
        }
        if (formData.newPassword !== formData.confirmPassword) {
          toast.error("New password and confirmation do not match.");
          return;
        }
        if (formData.newPassword.length < 8) {
          toast.error("New password must be at least 8 characters.");
          return;
        }

        const res = await axios.put(`/auth/resetPassword`, {
          email: formData.email,
          oldPassword: formData.oldPassword,
          newPassword: formData.newPassword,
        });

        if (res.status === 200) {
          toast.success("Password updated successfully!");
          setFormData((prev) => ({
            ...prev,
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          }));
          setShowPasswordSection(false);
        } else {
          toast.error("Error updating password!");
          return;
        }
      }

      const { bio, ...rest } = formData;
      onSave(rest);
    } catch (err) {
      console.error(err);
      toast.error("An unexpected error occurred.");
    }
  };

  return (
    <div className="UserProfileForm">
      <form onSubmit={(e) => e.preventDefault()}>
        <label htmlFor="full-name">
          Full Name<span className="required">*</span>
        </label>
        <input
          type="text"
          id="full-name"
          name="user_name"
          placeholder="Enter your Full Name"
          value={formData.user_name || ""}
          onChange={handleChange}
        />

        <label htmlFor="username">
          Username<span className="required">*</span>
        </label>
        <input
          type="text"
          id="username"
          name="username"
          placeholder="Enter your Username"
          value={formData.username || ""}
          onChange={handleChange}
        />

        <label htmlFor="email">
          Email Address<span className="required">*</span>
        </label>
        <input
          type="text"
          id="email"
          name="email"
          placeholder="Enter your Email Address"
          required
          value={formData.email || ""}
          onChange={handleChange}
        />

        <div className="form-row">
          <div className="input-container-UserProfile">
            <label htmlFor="dob">Date of Birth</label>
            <input
              type="date"
              id="dob"
              name="dob"
              value={formData.dob || ""}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]}
            />
          </div>
          <div className="input-container-UserProfile">
            <label htmlFor="gender" style={{ marginBottom: "10px" }}>
              Gender
            </label>
            <select
              id="gender"
              name="gender"
              value={formData.gender || ""}
              onChange={handleChange}
              style={{outline:"none", border:"none"}}
            >
              <option value="" disabled>
      Select
    </option>
              <option value="male">Male</option>
              <option value="female">Female</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>

        <div className="form-row">
          <div className="input-container-UserProfile">
            <label htmlFor="pronoun">Pronouns</label>
            <input
              type="text"
              id="pronoun"
              name="pronouns"
              value={formData.pronouns || ""}
              onChange={handleChange}
            />
          </div>

          <div className="input-container-UserProfile">
            <label htmlFor="country">Country</label>
            <input
              type="text"
              id="country"
              name="country"
              value={formData.country || ""}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="password-section">
          <button
            type="button"
            className="collapse-toggle"
            onClick={() => {
              setShowPasswordSection(prev => {
                const next = !prev;
                if (!next) {
                  setFormData(prev => ({
                    ...prev,
                    oldPassword: "",
                    newPassword: "",
                    confirmPassword: ""
                  }));
                }
                return next;
              });
            }}
            aria-expanded={showPasswordSection}
          >
            {showPasswordSection ? "▼ Change Password" : "▶ Change Password"}
          </button>

          {showPasswordSection && (
            <>
              <div className="collapsible-content">
                <div className="input-group">
                  <label htmlFor="old-password">
                    Old Password
                    <span className="required">
                      {(formData.oldPassword || formData.newPassword || formData.confirmPassword) && "*"}
                    </span>
                  </label>
                  <input
                    type={showPasswords ? "text" : "password"}
                    id="old-password"
                    name="oldPassword"
                    placeholder="Old Password"
                    value={formData.oldPassword || ""}
                    onChange={handleChange}
                  />
                </div>

                <div className="input-group">
                  <label htmlFor="new-password">
                    New Password
                    <span className="required">
                      {(formData.oldPassword || formData.newPassword || formData.confirmPassword) && "*"}
                    </span>
                  </label>
                  <input
                    type={showPasswords ? "text" : "password"}
                    id="new-password"
                    name="newPassword"
                    placeholder="New Password"
                    value={formData.newPassword || ""}
                    onChange={handleChange}
                  />
                  {formData.newPassword && formData.newPassword.length < 8 && (
                    <p className="validation-hint">Password must be at least 8 characters</p>
                  )}
                </div>

                <div className="input-group">
                  <label htmlFor="confirm-password">
                    Confirm New Password
                    <span className="required">
                      {(formData.oldPassword || formData.newPassword || formData.confirmPassword) && "*"}
                    </span>
                  </label>
                  <input
                    type={showPasswords ? "text" : "password"}
                    id="confirm-password"
                    name="confirmPassword"
                    placeholder="Confirm New Password"
                    value={formData.confirmPassword || ""}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className="button-row">
                <button
                  type="button"
                  onClick={() => setShowPasswords((prev) => !prev)}
                  className="toggle-password-btn"
                >
                  {showPasswords ? "Hide Passwords" : "Show Passwords"}
                </button>
              </div>
            </>
          )}
        </div>
      </form>

      <button
        type="submit"
        className="save-button"
        style={{
          width: "212px",
          height: "67px",
          marginLeft: "550px",
          marginTop: "30px",
          fontSize: "28px",
          borderRadius:"10px"
        }}
        onClick={handleSave}
      >
        Save
      </button>
    </div>
  );
};

export default UserProfile;
